interface SessionToken {
    value: string;
    expires_at: number;
}

interface SessionResponse {
    client_secret: SessionToken;
}

// Define specific event types expected from OpenAI via data channel
interface OpenAIEvent {
    type: string;
    [key: string]: any; // Allow other properties
}

interface TranscriptEvent extends OpenAIEvent {
    type: 'response.text.delta' | 'response.text.done';
    response: {
        output: { text: string }[];
    };
}

interface SessionUpdateEvent extends OpenAIEvent {
    type: 'session.updated';
    session: any; // Define more specific session type if needed
}

interface ResponseDoneEvent extends OpenAIEvent {
    type: 'response.done';
    response: any; // Define more specific response type if needed
}

type RealtimeEventCallback = (event: any) => void;

export class OpenAIRealtimeManager {
    private peerConnection: RTCPeerConnection | null = null;
    private dataChannel: RTCDataChannel | null = null;
    private audioElement: HTMLAudioElement | null = null;
    private currentToken: SessionToken | null = null;
    private mediaStream: MediaStream | null = null;
    private eventListeners: Map<string, RealtimeEventCallback[]> = new Map();
    private messageQueue: string[] = []; // Queue for messages sent before data channel opens

    constructor(
        private readonly baseUrl: string,
        private readonly options = {
            iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        }
    ) {
        if (!baseUrl) {
            throw new Error("OpenAIRealtimeManager requires a non-empty baseUrl for the worker.");
        }
    }

    private async getNewToken(): Promise<SessionToken> {
        const response = await fetch(`${this.baseUrl}/api/voice/session`, {
            method: 'POST'
        });

        if (!response.ok) {
            throw new Error('Failed to obtain session token');
        }

        const data = await response.json() as SessionResponse;
        this.currentToken = data.client_secret;
        return this.currentToken;
    }

    private async ensureValidToken(): Promise<string> {
        if (!this.currentToken || Date.now() >= this.currentToken.expires_at * 1000) { // Check expiration (assuming expires_at is in seconds)
            console.log('[RealtimeManager] Token expired or missing, getting new token...');
            const newToken = await this.getNewToken();
            return newToken.value;
        }
        return this.currentToken.value;
    }

    private async setupPeerConnection(): Promise<RTCPeerConnection> {
        const pc = new RTCPeerConnection(this.options);

        // Set up audio element for remote stream
        this.audioElement = document.createElement('audio');
        this.audioElement.autoplay = true;

        // Handle remote tracks
        pc.ontrack = (event) => {
            if (this.audioElement) {
                this.audioElement.srcObject = event.streams[0];
            }
            this.emit('track', event);
        };

        // Set up data channel
        this.dataChannel = pc.createDataChannel('oai-events');
        this.setupDataChannel();

        // Function to check and emit connected event
        const checkAndEmitConnected = () => {
            if (pc.iceConnectionState === 'connected' && this.dataChannel?.readyState === 'open') {
                console.log('[RealtimeManager] ICE connected and Data Channel open. Emitting connected event.');
                this.emit('connected', null);
            }
        };

        // Handle ICE connection state changes
        pc.oniceconnectionstatechange = () => {
            const state = pc.iceConnectionState;
            console.log('[RealtimeManager] ICE Connection State Change:', state);
            this.emit('iceConnectionState', state);
            if (state === 'failed') {
                this.emit('error', new Error('ICE connection failed'));
            } else if (state === 'disconnected') {
                 this.emit('warning', 'ICE connection temporarily disconnected');
            } else if (state === 'connected') {
                checkAndEmitConnected(); // Check if data channel is also open
            }
        };

        return pc;
    }

    private setupDataChannel() {
        if (!this.dataChannel) return;

        this.dataChannel.onmessage = (event) => {
            try {
                // Log raw incoming data
                console.log('[DataChannel Raw Message]:', event.data);

                const data = JSON.parse(event.data) as OpenAIEvent;
                console.log('[DataChannel Parsed Message]:', data);

                // Emit generic message event
                this.emit('message', data);

                // Emit specific events based on type
                switch (data.type) {
                    case 'session.created':
                    case 'session.updated':
                        this.emit('sessionUpdate', (data as SessionUpdateEvent).session);
                        break;
                    case 'input_audio_buffer.speech_started':
                        this.emit('speechStarted', null);
                        break;
                    case 'input_audio_buffer.speech_stopped':
                        this.emit('speechStopped', null);
                        break;
                    case 'response.text.delta':
                        const deltaText = (data as TranscriptEvent).response?.output?.[0]?.text || '';
                        console.log(`[DataChannel] Emitting transcriptDelta: "${deltaText}"`);
                        this.emit('transcriptDelta', deltaText);
                        break;
                    case 'response.text.done':
                        const doneTextFromTextDone = (data as TranscriptEvent).response?.output?.[0]?.text || '';
                        if (doneTextFromTextDone) { // Only emit if there's text
                            console.log(`[DataChannel] Emitting transcriptDone (from text.done): "${doneTextFromTextDone}"`);
                            this.emit('transcriptDone', doneTextFromTextDone);
                        }
                        break;
                    case 'response.output_item.done': // Handle transcript in output_item.done
                    case 'response.done': // Also handle transcript in response.done
                        const responseData = (data as ResponseDoneEvent).response;
                        const outputItem = responseData?.output?.[0]; // Assuming first output item
                        // Check if the output item has audio content with a transcript
                        if (outputItem?.content?.[0]?.type === 'audio' && outputItem?.content?.[0]?.transcript) {
                            const transcript = outputItem.content[0].transcript;
                            console.log(`[DataChannel] Emitting transcriptDone (from ${data.type}): "${transcript}"`);
                            this.emit('transcriptDone', transcript);
                        }
                        // Also emit the full response.done event if needed
                        if (data.type === 'response.done') {
                            this.emit('responseDone', responseData);
                        }
                        break;
                    default:
                        console.log(`[DataChannel] Unhandled event type: ${data.type}`);
                }

            } catch (error) {
                console.error('[DataChannel] Error parsing message:', error, 'Raw data:', event.data);
                this.emit('error', new Error(`Failed to parse server event: ${error}`));
            }
        };

        this.dataChannel.onopen = () => {
            console.log('[DataChannel] Opened');
            this.emit('dataChannelOpen', null);
            // Send any queued messages
            console.log(`[DataChannel] Processing ${this.messageQueue.length} queued messages...`);
            while(this.messageQueue.length > 0) {
                const msg = this.messageQueue.shift(); // Get first message
                if (msg && this.dataChannel?.readyState === 'open') { // Double check channel state
                     try {
                         console.log('[DataChannel] Sending queued message:', msg);
                         this.dataChannel.send(msg);
                     } catch (error) {
                         console.error('[DataChannel] Error sending queued message:', error);
                         // Optionally re-queue or emit error
                         this.emit('error', new Error(`Failed to send queued message: ${error}`));
                     }
                }
            }

            // Check if ICE is also connected
            if (this.peerConnection?.iceConnectionState === 'connected') {
                console.log('[RealtimeManager] Data Channel open and ICE connected. Emitting connected event.');
                 this.emit('connected', null);
            }
        };
        this.dataChannel.onclose = () => this.emit('dataChannelClose', null);
        this.dataChannel.onerror = (errorEvent) => {
             console.error('[DataChannel] Error:', errorEvent);
             this.emit('dataChannelError', errorEvent);
        };
    }

    public on(event: string, callback: RealtimeEventCallback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, []);
        }
        this.eventListeners.get(event)?.push(callback);
    }

    private emit(event: string, data: any) {
        const callbacks = this.eventListeners.get(event) || [];
        callbacks.forEach(callback => {
             try {
                 callback(data);
             } catch (error) {
                 console.error(`Error in event handler for '${event}':`, error);
             }
        });
    }

    public async connect(): Promise<void> {
        try {
            // Get microphone access
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: true
            });

            // Setup new peer connection
            this.peerConnection = await this.setupPeerConnection();

            // Add audio track
            const audioTrack = this.mediaStream.getAudioTracks()[0];
            this.peerConnection.addTrack(audioTrack, this.mediaStream);

            // Create and set local description
            const offer = await this.peerConnection.createOffer();
            await this.peerConnection.setLocalDescription(offer);

            // Get valid token
            const token = await this.ensureValidToken();

            // Get SDP answer from server
            const response = await fetch(`${this.baseUrl}/api/voice/rtc`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/sdp'
                },
                body: offer.sdp
            });

            if (!response.ok) {
                // Attempt to parse JSON error first
                let errorDetails = 'Unknown error fetching SDP answer';
                try {
                     const errorJson = await response.json();
                     errorDetails = errorJson.details || errorJson.error || errorDetails;
                } catch(e) {
                    // If JSON parsing fails, use text
                    errorDetails = await response.text();
                }
                throw new Error(errorDetails);
            }

            // Set remote description
            const answerSdp = await response.text();
            await this.peerConnection.setRemoteDescription({
                type: 'answer',
                sdp: answerSdp
            });

        } catch (error) {
            console.error('[RealtimeManager] Connection error:', error);
            this.emit('error', error);
            await this.disconnect(); // Attempt cleanup on connection error
            throw error;
        }
    }

    public async disconnect(): Promise<void> {
        console.log('[RealtimeManager] Disconnecting...');
        try {
            // Close data channel
            this.dataChannel?.close();
            this.dataChannel = null;

            // Stop media stream
            this.mediaStream?.getTracks().forEach(track => track.stop());
            this.mediaStream = null;

            // Close peer connection
            if (this.peerConnection) {
                this.peerConnection.ontrack = null;
                this.peerConnection.oniceconnectionstatechange = null;
                this.peerConnection.close();
                this.peerConnection = null;
            }

            // Clean up audio element
            if (this.audioElement) {
                this.audioElement.srcObject = null;
                this.audioElement = null;
            }

            this.currentToken = null; // Clear token on disconnect
            this.eventListeners.clear(); // Clear listeners
            this.emit('disconnected', null);
        } catch (error) {
            console.error('[RealtimeManager] Error during disconnect:', error);
            this.emit('error', error);
            // Don't re-throw here usually, as disconnect is often cleanup
        }
    }

    public isConnected(): boolean {
        // Check both peer connection and data channel state
        return (
            this.peerConnection?.connectionState === 'connected' &&
            this.dataChannel?.readyState === 'open'
        );
    }

    // Renamed generic sendMessage for clarity
    private sendDataChannelMessage(message: any): void {
        const messageString = JSON.stringify(message);
        
        // If channel is open, send immediately
        if (this.dataChannel?.readyState === 'open') {
             try {
                 console.log('[DataChannel] Sending:', message);
                 this.dataChannel.send(messageString);
             } catch (error) {
                 console.error('[DataChannel] Error sending message:', error);
                 this.emit('error', new Error(`Failed to send message: ${error}`));
             }
        } else {
            // Otherwise, queue the message
            console.error('Attempted to send message, but data channel is not open.', { message });
            console.log('[DataChannel] Queuing message:', message);
            this.messageQueue.push(messageString);
            // Emit a warning maybe, but don't emit the critical "Data channel not ready" error here
        }
    }

    // --- New Structured Client Action Methods ---

    /**
     * Updates the current session configuration.
     * @param options - Object containing session options to update (e.g., { instructions: "..." })
     */
    public updateSession(options: { instructions?: string; /* add other valid options */ }): void {
        const event = {
            type: "session.update",
            session: options
        };
        this.sendDataChannelMessage(event);
    }

    /**
     * Sends a text message to the conversation.
     * @param text - The user's text message.
     */
    public sendTextMessage(text: string): void {
        const event = {
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "user",
                content: [
                    {
                        type: "input_text",
                        text: text,
                    }
                ]
            },
        };
        this.sendDataChannelMessage(event);
    }

    /**
     * Requests the model to generate a response.
     * @param modalities - Array specifying the desired output types (e.g., ["audio", "text"] or ["text"]).
     */
    public requestModelResponse(modalities: ('audio' | 'text')[] = ['audio', 'text']): void {
        const event = {
            type: "response.create",
            response: {
                modalities: modalities
            },
        };
        this.sendDataChannelMessage(event);
    }

    public getAudioElement(): HTMLAudioElement | null {
        return this.audioElement;
    }

    /**
     * Returns the current session ID if available.
     */
    public getSessionId(): string | null {
        return this.currentToken?.value || null;
    }
} 